/**
 * TemplateCarousel.jsx - Template Card Carousel Component
 * 
 * Checklist Items Covered:
 * - D1: Carousel Shell
 * - D2: Template Staging Eyebrow
 * - D3: Template Count Label
 * - D4: Carousel Prev Button
 * - D5: Carousel Next Button
 * - D6: Page Indicator
 * - D7: Glass Card Template
 * - D8: Template Status Pill
 * - D9: Armed Badge
 * - D10: Template Name
 * - D11: Template Subject
 * - D12: Template Type
 * - D13: Preview Button
 * - D14: Empty State Card
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Mail, Zap } from 'lucide-react';
import { styles, COLORS } from './messagingStyles';

const TemplateCarousel = ({
  templates = [],
  selectedTemplateId,
  onSelectTemplate,
  onPreviewTemplate,
  filterStatus = 'all',
}) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Filter templates by status
  const filteredTemplates = templates.filter(t => {
    if (filterStatus === 'all') return true;
    const status = t.status === 'draft' ? 'draft' : 'ready';
    return status === filterStatus;
  });

  const handlePrev = () => {
    if (carouselIndex > 0 && !isAnimating) {
      setIsAnimating(true);
      setCarouselIndex(prev => prev - 1);
      setTimeout(() => setIsAnimating(false), 200);
    }
  };

  const handleNext = () => {
    if (carouselIndex < filteredTemplates.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setCarouselIndex(prev => prev + 1);
      setTimeout(() => setIsAnimating(false), 200);
    }
  };

  const getTemplateStatus = (template) => {
    return template?.status === 'draft' ? 'draft' : 'ready';
  };

  const currentTemplate = filteredTemplates[Math.min(carouselIndex, filteredTemplates.length - 1)];
  const isArmed = currentTemplate?.id === selectedTemplateId;
  const status = currentTemplate ? getTemplateStatus(currentTemplate) : 'draft';

  return (
    <div style={styles.templateCarouselShell} data-testid="template-carousel">
      {/* D2-D6: Carousel Header */}
      <div style={styles.carouselHeader}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* D2: Template Staging Eyebrow */}
          <div style={styles.carouselEyebrow}>Template Staging</div>
          
          {/* D3: Template Count Label */}
          <div style={styles.carouselCountLabel}>
            {filteredTemplates.length > 0
              ? `${filteredTemplates.length} template${filteredTemplates.length === 1 ? '' : 's'}`
              : 'No templates'}
          </div>
        </div>
        
        {filteredTemplates.length > 0 && (
          <div style={styles.carouselNavRow}>
            {/* D4: Carousel Prev Button */}
            <button
              style={{
                ...styles.carouselNavBtn,
                ...(carouselIndex === 0 ? styles.carouselNavBtnDisabled : {}),
              }}
              onClick={handlePrev}
              disabled={carouselIndex === 0}
              data-testid="carousel-prev"
            >
              <ChevronLeft size={14} color={carouselIndex === 0 ? '#C8C0B2' : COLORS.goldDark} />
            </button>
            
            {/* D6: Page Indicator */}
            <span style={styles.carouselPageLabel}>
              {carouselIndex + 1} / {filteredTemplates.length}
            </span>
            
            {/* D5: Carousel Next Button */}
            <button
              style={{
                ...styles.carouselNavBtn,
                ...(carouselIndex >= filteredTemplates.length - 1 ? styles.carouselNavBtnDisabled : {}),
              }}
              onClick={handleNext}
              disabled={carouselIndex >= filteredTemplates.length - 1}
              data-testid="carousel-next"
            >
              <ChevronRight size={14} color={carouselIndex >= filteredTemplates.length - 1 ? '#C8C0B2' : COLORS.goldDark} />
            </button>
          </div>
        )}
      </div>

      {/* D7-D14: Template Card or Empty State */}
      {filteredTemplates.length === 0 ? (
        // D14: Empty State Card
        <div style={styles.carouselEmptyCard} data-testid="carousel-empty">
          <Mail size={26} color={COLORS.gold} />
          <div style={styles.carouselEmptyTitle}>No templates yet</div>
          <div style={styles.carouselEmptyBody}>
            Import HTML or open AI Studio to create your first message.
          </div>
        </div>
      ) : (
        // D7: Glass Card Template
        <div
          style={{
            ...styles.glassCard,
            ...(isArmed ? styles.glassCardArmed : {}),
            transform: isAnimating ? 'scale(0.96)' : 'scale(1)',
            transition: 'all 0.2s ease',
          }}
          onClick={() => currentTemplate && onSelectTemplate(currentTemplate.id)}
          data-testid="template-glass-card"
        >
          {/* Glassmorphism effects */}
          {isArmed && <div style={styles.glassArmedNeonBorder} />}
          <div style={styles.glassInsetHighlight} />
          
          <div style={styles.glassCardContent}>
            {/* Top Row: Status + Armed Badge */}
            <div style={styles.glassTopRow}>
              {/* D8: Template Status Pill */}
              <div 
                style={{
                  ...styles.glassStatusPill,
                  ...(status === 'ready' ? styles.glassStatusReady : styles.glassStatusDraft),
                }}
              >
                <span 
                  style={{
                    ...styles.glassStatusText,
                    ...(status === 'ready' ? styles.glassStatusTextReady : styles.glassStatusTextDraft),
                  }}
                >
                  {status === 'ready' ? 'Ready' : 'Draft'}
                </span>
              </div>
              
              {/* D9: Armed Badge */}
              {isArmed && (
                <div style={styles.glassArmedBadge}>
                  <Zap size={11} color={COLORS.gold} />
                  <span style={styles.glassArmedText}>Armed</span>
                </div>
              )}
            </div>
            
            {/* D10: Template Name */}
            <div style={styles.glassCardTitle}>
              {currentTemplate?.name || 'Untitled'}
            </div>
            
            {/* D11: Template Subject */}
            <div style={styles.glassCardSubject}>
              {currentTemplate?.subject || 'No subject line'}
            </div>
            
            {/* Footer Row: Type + Actions */}
            <div style={styles.glassCardFooterRow}>
              {/* D12: Template Type */}
              <div style={styles.glassCardType}>
                {currentTemplate?.template_type || 'Email'}
              </div>
              
              <div style={styles.glassCardActions}>
                {/* D13: Preview Button */}
                <button
                  style={styles.glassActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    currentTemplate && onPreviewTemplate(currentTemplate);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                    e.currentTarget.style.borderColor = COLORS.gold;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
                    e.currentTarget.style.borderColor = COLORS.borderMedium;
                  }}
                  data-testid="template-preview-btn"
                >
                  <Eye size={12} color={COLORS.goldDark} />
                  <span style={styles.glassActionText}>Preview</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateCarousel;
